import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import type { EWalletBrand } from '../../../../types/registration-payment.types';
import {
  EWALLET_BRANDS,
  PAYMENT_METHOD_CONFIG
} from '../constants/payment-method.const';

interface SelectEWalletBrandStepProps {
  initialBrand: EWalletBrand | null;
  onConfirm: (brand: EWalletBrand) => void;
  onBack: () => void;
  isLoading?: boolean;
  noTopMargin?: boolean;
}

export function SelectEWalletBrandStep({
  initialBrand,
  onConfirm,
  onBack,
  isLoading = false,
  noTopMargin = false
}: Readonly<SelectEWalletBrandStepProps>) {
  const [selected, setSelected] = useState<EWalletBrand | null>(initialBrand);

  return (
    <div className="w-full max-w-md px-6 sm:px-0">
      <div className={noTopMargin ? 'mt-0' : 'mt-8'}>
        <button
          type="button"
          onClick={onBack}
          className="-ml-1 mb-3 flex items-center gap-1 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <span>Payment Method</span>
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Select an eWallet</h1>
        <p className="mt-2 text-sm text-gray-500">
          Choose a specific eWallet to proceed further.
        </p>
      </div>

      <div className="mt-8 flex flex-col gap-3">
        {EWALLET_BRANDS.map((brand) => {
          const config = PAYMENT_METHOD_CONFIG[brand];
          const isSelected = selected === brand;
          return (
            <button
              key={brand}
              type="button"
              onClick={() => setSelected(brand)}
              className={`flex w-full items-center justify-between rounded-2xl border-2 p-4 text-left shadow-sm transition-all ${
                isSelected
                  ? 'border-[#5CB891] bg-[#f0faf5]'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <img
                  src={config.logo}
                  alt={config.label}
                  className="h-10 w-14 rounded-md object-contain"
                />
                <span className="text-sm font-semibold text-gray-900">
                  {config.label}
                </span>
              </div>
              <div
                className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                  isSelected ? 'border-[#5CB891]' : 'border-gray-300'
                }`}
              >
                {isSelected && (
                  <div className="h-3 w-3 rounded-full bg-[#5CB891]" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer actions */}
      <div className="mt-6">
        <button
          type="button"
          disabled={selected === null || isLoading}
          onClick={() => selected && onConfirm(selected)}
          className={`w-full rounded-2xl px-2 py-4 font-semibold text-white transition-all duration-300 ease-out ${
            selected !== null && !isLoading
              ? 'bg-[#5CB891] hover:bg-[#4aa07c]'
              : 'cursor-not-allowed bg-gray-300'
          }`}
        >
          {isLoading ? 'Loading...' : 'Continue'}
        </button>
      </div>

      <div className="mt-4 flex justify-center">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <span>←</span>
          <span>Go back to select Payment Method</span>
        </button>
      </div>
    </div>
  );
}
